<template lang='pug'>
.layout

	.title(v-if='layout.title')
		.highlight-text(v-html='layout.title')

	.sub-title(v-if='subtitle')
		.highlight-text(v-html='subtitle')

	.input-row(
		v-for='( row, i ) in rows'
		:key='i'
	)
		.input-item(
			v-for='item in row'
			:class='item.className || ""'
			:style='item.style || {}'
		)
			//- render every item
			component(

				v-if='!item.type || item.field'

				:is='formInputs[fields[item.field].component]'
				:options='fields[item.field].options'
				:field='fields[item.field]'
				:errors='getErrors( item.field )'
				:class='item.className || ""'
				:data='data'

				:form='form'
				:form-value='value'
				:field-key='item'

				:last-tab-index='lastTabIndex'

				@set-tab-index='setTabIndex'

				v-model='localValue[item.field]'
				@data='v => addData( item.field, v )'
			)

			layout(
				v-else
				:layout='item'
				:value='localValue'
				:fields='fields'
				:form-inputs='formInputs'
				:errors='errors'
				:form='form'
				:startingData='startingData'
				:last-tab-index='lastTabIndex'
				:data='data'

				@set-tab-index='setTabIndex'

				@input='v => combineValues( v, "localValue" )'
				@data='v => combineValues( v, "data" )'
			)
</template>

<script>
import { clone, combine } from '@/lib/utils';

export default {
	name : 'layout',

	props : {
		layout : {
			type : [Array, Object]
		},
		fields : {
			type : Object,
		},
		formInputs : {
			type : Object,
		},
		value : {
			type : Object,
		},
		errors : {
			type : Object,
		},
		form : {
			type : Object,
		},
		startingData : {
			type : [Array, Object],
		},
		lastTabIndex : {
			type : Number,
		},
	},

	data : () => ( {
		data       : {},
		localValue : null,

		subtitle : null,
	} ),

	computed : {

		rows() {
			// we want to create an array of arrays
			// to simplify our markup. This computed
			// variable will make sure that every row
			// in the layout is an array

			// get the children
			const { children } = this.layout;

			// map children to ensure arrays
			const rowArrays = children.map( ( row ) => {

				// if it's already an array, then we can
				// return it.
				if ( Array.isArray( row ) ) {
					return row;
				}

				// return our row wrapped in an array
				return [row];
			} );

			// this block allows the layout to use
			// just strings to describe the field where
			// no other properties are necessary
			const componentizedRowArrays = rowArrays.map( row => row.map( ( item ) => {
				if ( typeof item === 'string' ) {
					// if the item in the array is just a string,
					// then create the object for the user
					return {
						field : item,
					};
				}

				// otherwise just return their input
				return item;
			} ) );

			return componentizedRowArrays;
		},

		fieldsArray() {

			return this.rows.reduce( ( array, row ) => {
				const rowFields = row.reduce( ( arr, item ) => {
					if ( !item.type ) {
						return arr.concat( [item.field] );
					}

					// otherwise it is probably a section
					// so we need to do extra logic
					if ( item.type === 'section' ) {

						// eslint-disable-next-line no-shadow
						const getFields = ( item ) => {
							if ( typeof item === 'string' ) {
								return [item];
							}

							if ( Array.isArray( item ) ) {
								// eslint-disable-next-line no-shadow
								return item.reduce( ( arr, a ) => arr.concat( getFields( a ) ), [] );
							}

							return [item.field];
						};

						// eslint-disable-next-line no-shadow
						const childFields = item.children.reduce( ( arr, child ) => arr.concat( getFields( child ) ), [] );

						return arr.concat( childFields );
					}

					return arr;
				}, [] );

				return array.concat( rowFields );
			}, [] );

		},

		childrenValues() {

			if ( !this.localValue ) {
				return null;
			}

			return this.fieldsArray.reduce( ( obj, field ) => {
				const added  = {};
				added[field] = this.localValue[field];

				return Object.assign( obj, added );
			}, {} );

		},

	},

	created() {
		this.localValue = clone( this.value );

		const { subtitle } = this.layout;

		if ( typeof subtitle === 'string' ) {
			this.subtitle = subtitle;

			return;
		}

		if ( typeof subtitle === 'function' ) {

			this.subtitle = 'Loading...';

			Promise.resolve()
				.then( () => subtitle() )
				.then( ( string ) => {
					this.subtitle = string;
				} );

		}

	},

	watch : {
		childrenValues : {
			immediate : true,
			deep      : true,
			handler( v ) {
				if ( !v ) {
					return;
				}

				this.$emit( 'input', v );
			}
		},

		data : {
			immediate : true,
			deep      : true,
			handler( v ) {
				this.$emit( 'data', v );
			}
		}
	},

	methods : {

		setTabIndex() {
			this.$emit( 'set-tab-index' );
		},

		combineValues( values, prop = 'localValue' ) {
			this[prop] = combine( this[prop], values );
		},

		addData( field, data ) {
			const added  = {};
			added[field] = data;

			this.combineValues( added, 'data' );
		},

		getErrors( field ) {
			const errors = this.errors[field];

			if ( errors === undefined ) {
				return {};
			}

			return errors;
		}

	}
};
</script>

<style lang='scss'>

.layout {
	padding: 0 30px;

	.layout {
		padding: 0;

		.title {
			padding: 0;

			.highlight-text {
				font-size: 12px;
				text-transform: uppercase;
				letter-spacing: 2px;
				text-align: left;
				font-weight: 600;
				padding-left: 5px;
			}
		}
	}

	.title {
		padding-left: 20px;
		margin-bottom: 15px;

		.highlight-text {
			font-size: 24px;
			font-weight: 500;
		}
	}

	> .sub-title {
		font-size: 16px;
		padding-left: 20px;
		margin-bottom: 15px;

		.highlight-text {
			font-weight: 500;
		}
	}

	.input-row {
		display: flex;

		+ .input-row {
			margin-top: 15px;
		}

		.input-item {
			flex: 1 1 0;

			> div {
				width: 100%;
			}

			+ .input-item {
				margin-left: 15px;
			}

			input, textarea {

				&::placeholder {
					color: $input-color-faded!important;
				}
			}
		}
	}
}
</style>
